import React, { memo } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';
import type {
	InlineCommentCreateComponentProps,
	InlineCommentViewComponentProps,
} from '@atlaskit/editor-plugins/annotation';

import { CommentEditor } from '@confluence/comment/entry-points/LazyCommentEditor.ts';
import { LoadableAfterPaint } from '@confluence/loadable';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import { getEditorResolvedState } from '@confluence/comments-util/entry-points/stateResolver.ts';

import { CreateManager } from './CreateManager.tsx';
import type { CreateComponentProps } from './CreateComponent.tsx';
import { ViewManager } from './ViewManager.tsx';
import type { ViewComponentProps } from './ViewComponent.tsx';

const CreateComponent = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CreateComponent" */ './CreateComponent.tsx'))
			.CreateComponent,
});

const ViewComponent = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ViewComponent" */ './ViewComponent.tsx'))
			.ViewComponent,
});

const ViewComponentMemoized = ({
	pageId,
	editorActions,
	getReactionMarkerMap,
}: {
	pageId: string;
	editorActions?: EditorActions;
	getReactionMarkerMap?: () => Record<string, string> | undefined;
}) =>
	memo((props: InlineCommentViewComponentProps) => {
		return (
			<ViewComponent
				pageId={pageId}
				editorActions={editorActions}
				{...props}
				reactionMarkerMap={getReactionMarkerMap?.()}
			/>
		);
	});

const CreateComponentMemoized = ({ pageId }: { pageId: string }) =>
	memo((props: InlineCommentCreateComponentProps) => {
		return <CreateComponent pageId={pageId} {...props} />;
	});

type GetEditorInlineCommentOptionsType = {
	pageId: string;
	editorActions: EditorActions;
	triggerAnalyticsEvent: any;
	updateUnresolvedInlineComment: (unresolvedInlineComments: string[]) => void;
	setEditorCreateProps: (
		createProps: (InlineCommentCreateComponentProps & CreateComponentProps) | null,
	) => void;
	setEditorViewProps: (
		viewProps: (InlineCommentViewComponentProps & ViewComponentProps) | null,
	) => void;
	editorAnnotationProviderManagersEnabled: boolean;
	getCanAddComments: () => boolean;
	contentType?: string;
	getReactionMarkerMap?: () => Record<string, string> | undefined;
	getOptimisticResolvedMarkerRefs?: () => string[] | undefined;
};

export const getEditorInlineCommentOptions = ({
	pageId,
	editorActions,
	triggerAnalyticsEvent,
	updateUnresolvedInlineComment,
	editorAnnotationProviderManagersEnabled,
	getCanAddComments,
	contentType,
	getReactionMarkerMap,
	getOptimisticResolvedMarkerRefs,
}: GetEditorInlineCommentOptionsType) => {
	let updateSubscriber = getEditorAnnotationEventEmitter();
	if (!updateSubscriber?.off) {
		updateSubscriber = null;
	}

	return {
		viewComponent: editorAnnotationProviderManagersEnabled
			? ViewManager({
					pageId,
					editorActions,
					getReactionMarkerMap,
				})
			: ViewComponentMemoized({ pageId, editorActions, getReactionMarkerMap }),
		createComponent: editorAnnotationProviderManagersEnabled
			? CreateManager({ pageId })
			: CreateComponentMemoized({ pageId }),
		getState: (annotations: any) => {
			const optimisticResolvedMarkerRefs = getOptimisticResolvedMarkerRefs?.();

			return getEditorResolvedState({
				annotations,
				pageId,
				triggerAnalyticsEvent,
				updateUnresolvedInlineComment,
				optimisticResolvedMarkerRefs,
			});
		},
		updateSubscriber,
		disallowOnWhitespace: true,
		isToolbarAbove: true,
		supportedBlockNodes: ['media'],
		onCommentButtonMount: () => CommentEditor.preload(),
		getCanAddComments,
		contentType,
	};
};
